import { useAppSelector } from 'reduxTyped'

import { getThreadsCounterForTab } from 'apps/chat/selectors/countersSelectors'
import { MIN_SUBSCRIBERS_FOR_BROADCAST_PROMOTION } from 'apps/dashboard/components/HomeTab/HomeTabBlockBanners/constants'
import { Ability } from 'common/core/constants/Ability'
import { ChannelType } from 'common/core/constants/ChannelType'
import { useAbility } from 'common/core/hooks/useAbility'
import AccountModel from 'common/core/models/Account'
import {
  getCurrentAccount,
  getHasAnyBroadcasts,
  isAccountBlocked,
  isTrialAvailable,
} from 'common/core/selectors/appSelectors'
import {
  getHasAnyStatusFBChannel,
  getIsInstagramOnlyEnabledNoEmailNoSms,
  getIsSmsOrEmailEnabled,
  getIsTiktokOnlyEnabled,
  hasConnectedInstagramChannel,
  isFBChannelConnected,
} from 'common/core/selectors/channelSelectors'
import { getIsAccountAdsEnabled } from 'common/core/selectors/currentAccountSelectors'
import { getIsSidebarHidden } from 'common/core/selectors/uiSelectors'
import { getReasonWithMoreSubscribers } from 'common/oneTimeNotify/selectors/notifyReasonSelectors'
import { getIsNeedToShowPayNow } from 'common/settings/selectors/settingsSelectors'
import { getUserRole } from 'common/userRoles'
import { useMediaQuery } from 'utils/commonHooks'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

export const useNavParams = () => {
  const enableIGRecurringNotification = useAccountReleaseToggle(
    AccountFlag.ENABLE_IG_RECURRING_NOTIFICATION,
  )

  const isAiAlwaysOnAutomationEnabled = useAccountReleaseToggle(
    AccountFlag.ENABLE_AI_ALWAYS_ON_AUTOMATION,
  )

  const isAiAutomationBetaRecruitmentEnabled = useAccountReleaseToggle(
    AccountFlag.ENABLE_AI_AUTOMATION_BETA_TESTER_RECRUITMENT,
  )

  const account = useAppSelector(getCurrentAccount)
  const isPRO = AccountModel.isProOrTrial(account)
  const agencyAnalyticsAbility = useAbility(Ability.AGENCY_ANALYTICS)
  const userRole = useAppSelector(getUserRole)
  const openedThreads = useAppSelector(getThreadsCounterForTab)
  const isSidebarHidden = useAppSelector(getIsSidebarHidden)
  const isInstagramOnlyEnabled = useAppSelector(getIsInstagramOnlyEnabledNoEmailNoSms)
  const isTiktokOnlyEnabled = useAppSelector(getIsTiktokOnlyEnabled)
  const hasAnyStatusFBChannel = useAppSelector(getHasAnyStatusFBChannel)
  const hasConnectedFBChannel = useAppSelector(isFBChannelConnected)
  const hasConnectedIGChannel = useAppSelector(hasConnectedInstagramChannel)
  const isSMSorEmailEnabled = useAppSelector(getIsSmsOrEmailEnabled)
  const isBlocked = useAppSelector(isAccountBlocked)
  const isAdsEnabled = useAppSelector(getIsAccountAdsEnabled)
  const hasIGRn = enableIGRecurringNotification && hasConnectedIGChannel
  const hasNotBroadcasts = !useAppSelector(getHasAnyBroadcasts)
  const channelForCalculation = hasIGRn ? ChannelType.INSTAGRAM : ChannelType.FB
  const topicWithMoreSubscribers = useAppSelector((state) =>
    getReasonWithMoreSubscribers(state, channelForCalculation),
  )
  const isRetryPayment = useAppSelector(getIsNeedToShowPayNow)
  const maxTopicSubscribersCount = topicWithMoreSubscribers?.value ?? 0
  const hasEnoughOptIns = maxTopicSubscribersCount > MIN_SUBSCRIBERS_FOR_BROADCAST_PROMOTION

  const hasCompletedRNOnBoarding =
    (hasConnectedFBChannel || hasIGRn) && hasNotBroadcasts && hasEnoughOptIns

  const isContactsLimitBanner = AccountModel.isFree(account) || isBlocked
  const hasTrial = useAppSelector(isTrialAvailable)

  const isMobileWidth = useMediaQuery('(max-width: 768px)')

  const isNewNavigationEnabled = useAccountReleaseToggle(AccountFlag.ENABLE_NEW_NAVIGATION)

  return {
    isPRO,
    allowAnalyticsTabFlag: agencyAnalyticsAbility.isEnabled,
    userRole,
    openedThreads,
    pageId: account.page_id,
    isSidebarHidden,
    isInstagramOnlyEnabled,
    isTiktokOnlyEnabled,
    hasAnyStatusFBChannel,
    hasCompletedRNOnBoarding, // Will be used in a broadcast notification circle
    isSMSorEmailEnabled,
    enableIGRecurringNotification,
    isAdsEnabled,
    isAiAlwaysOnAutomationEnabled,
    isAiAutomationBetaRecruitmentEnabled,
    isContactsLimitBanner,
    isRetryPayment,
    hasTrial,
    isMobileWidth,
    isNewNavigationEnabled,
  }
}
